import { useState } from "react";
import {
  GlobeHemisphereEast, Phone, WhatsappLogo, ShieldCheck, FileText, Scales,
  SealCheck, Warning, Buildings, Handshake,
} from "@phosphor-icons/react";
import { GoogleReviews } from "../components/GoogleReviews";
import { LeadForm } from "../components/LeadForm";
import {
  useSeo, Container, PlanHero, SectionTitle, CheckItem, StepCard, FaqList, RefundBadge, money,
} from "../components/plans/PlanShared";

const ACCENT = "#F59E0B";
const WA = "https://wa.me/919986143554?text=" + encodeURIComponent("Hi Unicators, I'm interested in the NRI Counselling plan. Please share the details.");

const HANDLED = [
  "End-to-end NRI documentation preparation & verification",
  "Eligibility assessment for genuine NRI & NRI-sponsored candidates",
  "Verified college data with transparent fee clarity",
  "Management & NRI-quota seat strategy across states",
  "Best-possible negotiation support where applicable",
  "Physical verification support by our senior team when required",
  "Round-wise alerts for stray & vacant NRI/Deemed seats",
  "Powered by Unicators' seat-matrix, fees & cut-off intelligence",
];

const STEPS = [
  { n: 1, title: "Eligibility Check", body: "We verify your NRI status and sponsor relationship to confirm genuine eligibility before anything else." },
  { n: 2, title: "Document Preparation", body: "Complete, error-free NRI documentation handled end-to-end — the #1 reason admissions get rejected." },
  { n: 3, title: "College & Fee Strategy", body: "Verified private/deemed college data with clear fees so you never overpay for a seat." },
  { n: 4, title: "Choice Filling & Rounds", body: "Expert choice-filling and live round support across NRI, management and deemed quotas." },
  { n: 5, title: "Seat Confirmation", body: "We stay with you through allotment, fee payment and reporting until your admission is confirmed." },
];

const FAQ = [
  { q: "Who is the NRI Counselling plan for?", a: "It is exclusively for genuine NRI candidates, children of NRIs, and valid 1st or 2nd-degree NRI-sponsored candidates. We strictly do not support fake or manipulated documents." },
  { q: "Why is expert handling so important for NRI seats?", a: "NRI and management admissions offer strong opportunities but are highly documentation-sensitive. Small errors cause rejections despite low cut-offs — expert handling protects your seat and your money." },
  { q: "Does the fee include travel/verification support?", a: "Yes — where physical verification is required, senior-team support and associated travel are included in the plan." },
  { q: "Is there a refund policy?", a: "If no seat is secured across all rounds, you may carry forward our services to the next year or claim a refund as per our terms. A seat that is allotted but refused is not eligible." },
];

export default function PlansNRICounselling() {
  useSeo(
    "NRI & Management Quota NEET Counselling — Expert, Ethical Handling | Unicators",
    "Specialised NRI NEET UG counselling with end-to-end documentation, eligibility assessment and management-quota strategy. Ethical, expert handling for genuine NRI candidates.",
  );
  const [leadOpen, setLeadOpen] = useState(false);

  return (
    <div className="bg-white" data-testid="plans-nri-page">
      <PlanHero
        accent={ACCENT} bgFrom="#FFF7ED" bgTo="#FEFCE8"
        eyebrow="Specialised · NRI & Management Quota"
        title="Secure high-value seats"
        highlight="with zero-error handling."
        subtitle="NRI and management admissions open strong opportunities — but only when handled with correct documentation and expert execution. We handle the complexity, ethically and end-to-end."
        stats={[
          { value: "End-to-end", label: "Documentation" },
          { value: "Verified", label: "College & fee data" },
          { value: "100% Ethical", label: "Genuine cases only" },
          { value: "Senior Team", label: "Direct handling" },
        ]}
      >
        <button onClick={() => setLeadOpen(true)} data-testid="hero-book-call"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-full text-white font-bold text-sm uppercase tracking-wide shadow-[3px_3px_0px_0px_#09090B] hover:-translate-y-0.5 transition-transform" style={{ backgroundColor: ACCENT }}>
          <Phone size={16} weight="fill" /> Talk to an expert
        </button>
        <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#25D366] text-white font-bold text-sm uppercase tracking-wide hover:bg-[#20b957] transition-colors">
          <WhatsappLogo size={18} weight="fill" /> WhatsApp us
        </a>
      </PlanHero>

      {/* Pricing card */}
      <section className="py-16 sm:py-20">
        <Container>
          <SectionTitle center accent={ACCENT} eyebrow="Transparent pricing" title="One specialised, all-inclusive plan" />
          <div className="max-w-xl mx-auto rounded-3xl border-2 border-[#F59E0B] p-8 shadow-[0_20px_50px_-15px_rgba(245,158,11,0.45)] bg-white text-center" data-testid="nri-plan-card">
            <span className="w-14 h-14 rounded-2xl bg-[#F59E0B]/10 flex items-center justify-center mx-auto">
              <GlobeHemisphereEast size={28} weight="fill" style={{ color: ACCENT }} />
            </span>
            <h3 className="mt-4 text-xl font-black text-[#09090B]">NRI Counselling Plan</h3>
            <p className="text-sm text-zinc-500 mt-1">Complete NRI & management-quota admission handling</p>
            <div className="mt-5 flex items-end justify-center gap-3">
              <span className="text-5xl font-black text-[#09090B] font-heading">{money(125000)}</span>
              <span className="text-lg text-zinc-400 line-through mb-1.5">{money(250000)}</span>
            </div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 mt-1">All-inclusive · one-time payment</p>
            <div className="mt-4 flex justify-center"><RefundBadge accent={ACCENT} text="100% refund policy (terms applicable)" /></div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button onClick={() => setLeadOpen(true)} data-testid="nri-enroll"
                className="flex-1 h-12 rounded-full bg-[#F59E0B] text-white font-bold text-sm uppercase tracking-wide flex items-center justify-center gap-2 shadow-[3px_3px_0px_0px_#09090B] hover:-translate-y-0.5 transition-transform">
                <Phone size={15} weight="fill" /> Request callback
              </button>
              <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="nri-whatsapp"
                className="flex-1 h-12 rounded-full bg-[#25D366] text-white font-bold text-sm uppercase tracking-wide flex items-center justify-center gap-2 hover:bg-[#20b957] transition-colors">
                <WhatsappLogo size={17} weight="fill" /> WhatsApp
              </a>
            </div>

            <ul className="mt-7 space-y-2.5 text-left">
              {HANDLED.map((f, i) => (<CheckItem key={i} accent={ACCENT}>{f}</CheckItem>))}
            </ul>
          </div>
        </Container>
      </section>

      {/* Process */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-[#FEFCE8] to-white">
        <Container>
          <SectionTitle center accent={ACCENT} eyebrow="How we handle it"
            title="Precision handling, start to finish"
            sub="Where most NRI admissions fail on documentation, we bring structure and expertise." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {STEPS.map((s) => (<StepCard key={s.n} {...s} accent={ACCENT} />))}
          </div>
        </Container>
      </section>

      {/* Ethics callout */}
      <section className="py-14">
        <Container>
          <div className="rounded-3xl border border-amber-200 bg-amber-50/60 p-8 flex items-start gap-4 max-w-4xl">
            <span className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shrink-0 border border-amber-200">
              <SealCheck size={24} weight="fill" style={{ color: ACCENT }} />
            </span>
            <div>
              <h3 className="text-lg font-bold text-[#09090B]">100% Ethical &amp; Genuine Process</h3>
              <p className="text-sm text-[#52525B] mt-2 leading-relaxed">
                We work only with genuine NRI eligibility cases. We do not support fake or manipulated documents, as
                this can lead to admission cancellation and serious legal consequences. This service is designed for
                genuine NRI candidates, children of NRIs, and valid 1st or 2nd-degree NRI-sponsored candidates.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-[#FFF7ED]">
        <Container>
          <SectionTitle accent={ACCENT} eyebrow="Good to know" title="Frequently asked questions" />
          <FaqList items={FAQ} accent={ACCENT} />
        </Container>
      </section>

      <GoogleReviews />
      <LeadForm open={leadOpen} onOpenChange={setLeadOpen} plan="NRI Counselling (₹1,25,000)" callbackType="NRI" />
    </div>
  );
}
